/**
 * /about — "Principles behind Kalib AI"
 *
 * Public, voice-led page mirroring the LandingPage chrome (cream
 * palette, amber accents, beta badge, single primary CTA in the
 * header). Content is intentionally inline JSX rather than markdown:
 *   1. Bold lead phrases + italics need precise control
 *   2. The 10 principles read as one continuous editorial spread, not
 *      a CMS list, so collapsing it into renderable data fields would
 *      lose voice
 *
 * Editing copy: edit `PRINCIPLES` below. The numbered chrome and
 * spacing comes from the renderer; you only change the text. Each
 * entry must have:
 *   - lead   : bolded headline phrase, ends with a full stop
 *   - body   : the rest of the paragraph; <em> nodes preserved
 *   - badge? : optional small amber pill (used for "Coming soon")
 */
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BetaBadge } from "@/components/BetaBadge";
import { KalibLogo } from "@/components/KalibLogo";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const BRAND = "Kalib AI";

const Eyebrow = ({ children }) => (
  <span
    className="text-[11px] font-semibold uppercase tracking-widest"
    style={{ color: "#b8790f", letterSpacing: "0.16em" }}
  >
    {children}
  </span>
);

const PRINCIPLES = [
  {
    lead: "The client is the moral centre.",
    body: (
      <>
        Kalib AI puts the Account front and centre in the Reveal language. They are <em>serious</em>, <em>constrained</em> and <em>rational</em>, not someone to be "convinced" or "sold to". Our AI architecture in Kalib AI tries to frame everything around the constraint most active for them right now. These could be <em>budget, governance, sequencing, risk</em>, …
      </>
    ),
  },
  {
    lead: "Calibration, not coaching.",
    body: (
      <>
        Coaching implies the user is deficient. We <em>do not think you are</em>. Calibration is what you do to an instrument that's already accurate, to match current conditions.
      </>
    ),
  },
  {
    lead: "Calibrated to you, not a generic seller.",
    body: (
      <>
        We want to produce Reveals that make sense to you, whether you are "senior" or new. Account-deep or account-fresh. Industry-fluent or learning the vocabulary. Thirty years in or three. The same public signals mean different things depending on who's reading them — and matching that is <em>{BRAND}'s</em> work.
      </>
    ),
  },
  {
    lead: "Pen portraits, not a generic role.",
    body: (
      <>
        The prompt bank architecture holds detailed pen portraits for a number of 'lens' combinations and permutations — SaaS hunter, services farmer, solutioning lead, delivery partner. They do two jobs: shape how output reads for your role, and act as a benchmark for what a strong operator in that role looks like at the top of their game.
      </>
    ),
  },
  {
    lead: "Fit, not fix.",
    body: (
      <>
        Tell us how you currently operate — and where you're trying to take the account — and Kalib calibrates accordingly. Not to grade you. Not to teach you. The lift comes from sharper inputs matched to your move and shaped by what the strongest operators in your archetype tend to focus on.
      </>
    ),
    badge: "Coming soon",
  },
  {
    lead: "Public-signal humility.",
    body: (
      <>
        Kalib has no idea who was in the last QBR, what your delivery health is, or whether your renewal is wobbly. It works on public evidence and your stated profile. That boundary is structural and deliberate, not a disclaimer.
      </>
    ),
  },
  {
    lead: "Optionality, not orders.",
    body: (
      <>
        "Test these three angles" beats "pursue this opportunity." We hold the signal reading. You hold the move. Anything else is fake authority dressed up as confidence.
      </>
    ),
  },
  {
    lead: "Every signal earns its bridge.",
    body: (
      <>
        A BNP move is not a Deutsche Bank prediction. We explain why a signal is a benchmark, a pressure point, a timing cue, or a lens for stress-testing your story. No random pattern matching with a confident voiceover.
      </>
    ),
  },
  {
    lead: "Calibrated to AI-era buyers.",
    body: (
      <>
        The AI-era buyer has moved. The client-facing playbook should, too. Hypotheses, not capability decks. Workflow framing, not feature tours. Economic proof earlier. Governance before it becomes a blocker. {BRAND} is calibrated to how AI-era buyers in financial services actually behave, not to how they used to be sold to.
      </>
    ),
  },
  {
    lead: "Useful before impressive.",
    body: (
      <>
        The reaction we work for is "I can use this." Not "interesting, the system has read a lot." Kalib isn't a strategy essay engine, and it isn't trying to win a synthesis prize.
      </>
    ),
  },
];

export default function AboutPage() {
  const { user } = useAuth();
  const primaryCtaTo = user ? "/intelligence" : "/signup";
  const primaryCtaLabel = user ? "Open Reveal" : "Set up your Reveal";

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f6f0e6", color: "#24211d" }} data-testid="about-page">
      {/* Header — matches LandingPage so the page feels first-party */}
      <header
        className="sticky top-0 z-30"
        style={{ backgroundColor: "#f6f0e6", borderBottom: "1px solid #ddd3c2" }}
      >
        <div className="max-w-6xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="about-logo-link">
            <KalibLogo variant="light" height={28} testId="about-header-logo" />
            <BetaBadge />
          </Link>
          <nav className="flex items-center gap-6 sm:gap-8">
            <Link
              to="/about"
              className="text-[11px] font-semibold uppercase transition-colors"
              style={{ letterSpacing: "0.15em", color: "#b8790f" }}
              aria-current="page"
              data-testid="about-header-self-link"
            >
              Principles
            </Link>
            {!user && (
              <Link
                to="/signin"
                className="text-[11px] font-semibold uppercase transition-colors"
                style={{ letterSpacing: "0.15em", color: "#3a342e" }}
                data-testid="about-header-signin-link"
              >
                Sign in
              </Link>
            )}
            <Link to={primaryCtaTo} data-testid="about-header-cta">
              <Button
                className="rounded-none text-[12px] font-medium px-4 py-2 text-white hover:opacity-90"
                style={{ backgroundColor: "#24211d" }}
              >
                {primaryCtaLabel}
                <ArrowRight className="w-3.5 h-3.5 ml-1.5 inline" strokeWidth={2.5} />
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero — eyebrow + H1 + lede */}
      <section className="max-w-3xl mx-auto px-6 pt-16 sm:pt-24 pb-12">
        <Eyebrow>Principles</Eyebrow>
        <h1
          className="font-heading mt-4 tracking-tight"
          style={{
            fontSize: "clamp(2rem, 4vw + 1rem, 3.5rem)",
            lineHeight: 1.05,
            color: "#24211d",
            fontWeight: 700,
            letterSpacing: "-0.02em",
          }}
          data-testid="about-headline"
        >
          Principles behind {BRAND}
        </h1>
        <div
          className="mt-7 space-y-5"
          style={{ fontSize: "17px", lineHeight: 1.7, color: "#3a342e" }}
          data-testid="about-lede"
        >
          <p>
            {BRAND} sits between public market signals and your next client conversation. Kalib is a calibration layer, built so a client-facing operator walks into the room with sharper judgement than the public web alone can give. We use <em>calibration</em> deliberately. What client-facing people don't always have is a structured way to translate what's moving in the public domain — what peer banks are signalling, where regulators are pointing, which technology bets are landing, how the competitive picture is shifting — into a confident, account-specific line for this week or this quarter. That translation gap is the work here at {BRAND}.
          </p>
          <p>
            The <em>Weekly Account Reveals</em> are a work in progress. The principles behind them aren't.
          </p>
        </div>
      </section>

      {/* The 10 Principles */}
      <section className="max-w-3xl mx-auto px-6 pb-12 sm:pb-20" data-testid="about-principles-section">
        <div className="space-y-1">
          {PRINCIPLES.map((p, idx) => (
            <article
              key={idx}
              className="grid grid-cols-12 gap-4 sm:gap-6 py-7 sm:py-8"
              style={{ borderTop: idx === 0 ? "1px solid #ddd3c2" : "1px solid #e8dec9" }}
              data-testid={`about-principle-${idx + 1}`}
            >
              {/* Number — set in editorial-pull-quote scale */}
              <div className="col-span-2 sm:col-span-1">
                <span
                  className="font-mono"
                  style={{
                    fontSize: "22px",
                    fontWeight: 600,
                    color: "#b8790f",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {String(idx + 1).padStart(2, "0")}
                </span>
              </div>
              <div className="col-span-10 sm:col-span-11">
                <div className="flex items-baseline gap-3 flex-wrap mb-2">
                  <p
                    className="font-heading"
                    style={{
                      fontSize: "19px",
                      fontWeight: 700,
                      color: "#24211d",
                      lineHeight: 1.3,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {p.lead}
                  </p>
                  {p.badge && (
                    <span
                      className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest"
                      style={{
                        backgroundColor: "#fdf3df",
                        border: "1px solid #d69b22",
                        color: "#92400E",
                        letterSpacing: "0.14em",
                      }}
                      data-testid={`about-principle-${idx + 1}-badge`}
                    >
                      {p.badge}
                    </span>
                  )}
                </div>
                <p style={{ fontSize: "15.5px", lineHeight: 1.7, color: "#3a342e" }}>
                  {p.body}
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Compounding effect — closing block, dark band */}
      <section
        className="relative"
        style={{ backgroundColor: "#1c1916", color: "#f6f0e6" }}
        data-testid="about-compounding-section"
      >
        <div className="max-w-3xl mx-auto px-6 py-16 sm:py-20">
          <span
            className="text-[11px] font-semibold uppercase tracking-widest"
            style={{ color: "#d69b22", letterSpacing: "0.16em" }}
          >
            The compounding effect
          </span>
          <div
            className="mt-6 space-y-5"
            style={{ fontSize: "17px", lineHeight: 1.7, color: "#d6cfc1" }}
          >
            <p>
              "I can use this." "This is getting better week after week." If a sharp client-facing operator reads our output and thinks anything else, we missed.
            </p>
            <p>
              {BRAND} is designed to compound. The same account, read week after week, builds a picture no single session can replicate. Patterns emerge. Timing clarifies. What looked like noise starts to look like a direction. The first Reveal is useful. The tenth is an unfair advantage.
            </p>
          </div>
          <p
            className="font-heading mt-10"
            style={{ fontSize: "22px", fontWeight: 600, color: "#f6f0e6", letterSpacing: "-0.01em" }}
            data-testid="about-signoff"
          >
            We'll see you next week.
          </p>
          <div className="mt-10">
            <Link to={primaryCtaTo} data-testid="about-bottom-cta">
              <Button
                className="rounded-none text-[14px] font-medium px-6 py-3 text-white inline-flex items-center gap-2"
                style={{ backgroundColor: "#d69b22" }}
              >
                {primaryCtaLabel}
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
